import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {} from "@callveriq/type/lms";
import {
    Event,
    EventsResponse,
    IChatChannelResponse,
} from "@callveriq/type/SupervisorAssist";
import { GlobalFiltersStateType } from "@callveriq/contexts/GlobalFiltersStateContextProvider";
import {
    getDomain,
    grpcPayloadConstructor,
} from "@callveriq/utils/helper/common.helper";
import { LiveTranscriptType } from "@callveriq/type/WebSocketNotification";
import axiosBaseQuery from "../axiosBaseQuery";

export const supervisorAssistApiSlice = createApi({
    reducerPath: "supervisorAssistApiSlice",
    baseQuery: axiosBaseQuery({
        transformResponse: (response) => response,
    }),
    keepUnusedDataFor: 0,
    endpoints: (builder) => ({
        getCalls: builder.query<
            EventsResponse,
            Pick<GlobalFiltersStateType, "reps" | "teams"> & {
                overall_sentiment?: "eq:negative";
                violations?: boolean;
                user_id: number;
                gp_overall_sentiment?: "eq:negative";
            }
        >({
            query: ({ user_id, teams, reps, ...rest }) => {
                const filter = grpcPayloadConstructor({
                    ...(teams.length && { team_id: teams }),
                    ...(reps.length && { agent_id: reps }),
                    ...rest,
                });

                return {
                    url: "live_assist/live_proxy/event/list",
                    method: "POST",
                    body: {
                        user_id,
                        ...(filter.length && {
                            filter,
                        }),
                    },
                };
            },
        }),
        getCallByID: builder.query<
            Event,
            { conversation_id: string; user_id: number }
        >({
            query: (payload) => {
                return {
                    url: `live_assist/live_proxy/event/${getDomain()}_${
                        payload.conversation_id
                    }`,
                    method: "GET",
                    params: {
                        user_id: payload.user_id,
                    },
                };
            },
            transformResponse: (response: { data: Event; message: string }) => {
                return { ...response.data };
            },
        }),
        getChatChannels: builder.query<IChatChannelResponse, number>({
            query: (supervisor_id) => ({
                url: "live_assist/live_proxy/bark/get_supversior_channels",
                method: "POST",
                body: {
                    pageToken: "",
                    pageSize: 10,
                    TenantId: getDomain(),
                    SupervisorId: `${supervisor_id}`,
                },
            }),
            transformResponse: (response: IChatChannelResponse) => {
                return Array.isArray(response.getChannelResult.data)
                    ? {
                          ...response,
                          getChannelResult: {
                              ...response.getChannelResult,
                              data: response?.getChannelResult?.data?.map(
                                  (e) => ({
                                      ...e,
                                      isInitiated: e.isInitiated || false,
                                      isTyping: false,
                                  })
                              ),
                          },
                      }
                    : {
                          getChannelResult: {
                              ...response.getChannelResult,
                              data: [],
                          },
                      };
            },
        }),

        getChatHistoryByChannelId: builder.query<
            unknown | null, // to-do
            { channelId: string }
        >({
            queryFn: () => ({ data: null }),
        }),

        getAgentOnlineStatusById: builder.query<boolean, { agentId: number }>({
            queryFn: () => ({ data: false }),
        }),

        getLiveConversationTranscript: builder.query<
            {
                live_mpd_url?: string;
                data: Pick<
                    LiveTranscriptType["body"]["message"],
                    "result_start_time" | "result_end_time" | "transcription"
                >[];
                length: number;
            },
            { channel_id: string }
        >({
            query: ({ channel_id }) => ({
                url: `live_assist/live_proxy/event/transcripts/${channel_id}`,
                method: "GET",
            }),
        }),
    }),
});

export const {
    useGetCallsQuery,
    useGetChatChannelsQuery,
    useGetLiveConversationTranscriptQuery,
    useGetCallByIDQuery,
} = supervisorAssistApiSlice;
